import { useState, useContext, useRef } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import PrimaryButton from "../../../components/Button/PrimaryButton";
import AuthFormLayout from "../../../components/Auth/AuthFormLayout";
import AuthField from "../../../components/Auth/AuthField";
import MfaChallengeForm from "../../../components/Auth/MfaChallengeForm";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { AUTH_MESSAGES } from "../../../utils/constants/authMessages";

const REQUIRED_MESSAGES = {
  identifier: "Email is required.",
  password: "Password is required.",
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const AdminLogin = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const captchaRef = useRef(null);

  const [form, setForm] = useState({ identifier: "", password: "" });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Turned on by the API once this client has failed too many times.
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [mfaToken, setMfaToken] = useState("");

  const validate = () => {
    const currentErrors = {};
    Object.keys(REQUIRED_MESSAGES).forEach((field) => {
      if (!form[field]) currentErrors[field] = REQUIRED_MESSAGES[field];
    });
    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: value ? "" : REQUIRED_MESSAGES[name] }));
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
    setServerError("");
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaToken("");
  };

  const routeAfterLogin = async () => {
    const meRes = await api.get("/auth/me", { withCredentials: true });
    setUser(meRes.data);

    const roleSlug = meRes.data?.roleSlug;
    if (roleSlug === "vendor" || roleSlug === "client") {
      navigate("/");
    } else if (meRes.data.setup_completed) {
      navigate("/");
    } else {
      navigate("/setup");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (captchaRequired && RECAPTCHA_SITE_KEY && !captchaToken) {
      setServerError("Please complete the CAPTCHA to continue.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post(
        "/auth/login",
        { ...form, captchaToken },
        { withCredentials: true }
      );

      resetCaptcha();
      setCaptchaRequired(false);

      if (res.data?.mfaRequired) {
        setMfaToken(res.data.mfaToken);
        setSubmitting(false);
        return;
      }

      await routeAfterLogin();
    } catch (err) {
      const data = err.response?.data;
      if (data?.captchaRequired) setCaptchaRequired(true);
      resetCaptcha();

      let message = data?.error || AUTH_MESSAGES.invalidCredentials;
      if (typeof data?.attemptsRemaining === "number") {
        message += ` ${data.attemptsRemaining} attempt${
          data.attemptsRemaining === 1 ? "" : "s"
        } remaining before this account is temporarily locked.`;
      }

      setServerError(message);
      setSubmitting(false);
    }
  };

  if (mfaToken) {
    return (
      <MfaChallengeForm
        mfaToken={mfaToken}
        onVerified={routeAfterLogin}
        onRestart={(message) => {
          setMfaToken("");
          setForm({ ...form, password: "" });
          setServerError(message || AUTH_MESSAGES.invalidCredentials);
        }}
      />
    );
  }

  return (
    <AuthFormLayout
      title="Log In"
      error={serverError}
      onSubmit={handleSubmit}
      footer={
        <p className="text-center text-xs font-medium text-gray-500">
          Don't you have an account?{" "}
          <Link to="/create-account" className="font-bold text-brand-600 hover:underline">
            Sign Up
          </Link>
        </p>
      }
    >
      <AuthField
        name="identifier"
        label="Email"
        placeholder="Email"
        autoComplete="username"
        value={form.identifier}
        error={errors.identifier}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <AuthField
        name="password"
        label="Password"
        type="password"
        placeholder="Password"
        autoComplete="current-password"
        value={form.password}
        error={errors.password}
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {captchaRequired && RECAPTCHA_SITE_KEY && (
        <div className="flex justify-center">
          <ReCAPTCHA
            ref={captchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(value) => setCaptchaToken(value || "")}
            onExpired={() => setCaptchaToken("")}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Link
          to="/request-reset"
          className="text-xs font-semibold text-brand-600 hover:underline"
        >
          Forgot Password?
        </Link>
      </div>

      <PrimaryButton type="submit" disabled={submitting}>
        {submitting ? "Logging In..." : "Log In"}
      </PrimaryButton>
    </AuthFormLayout>
  );
};

export default AdminLogin;
